import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { IdleService } from '../core/services/idle.service';
import { environment } from 'src/environments/environment';
import { polygon } from 'viem/chains';
import { polygonAmoy } from '../shared/helpers/polygonAmoy';
import { ContractIntegrationsService } from './contract-integrations.service';
import { StorageService } from './storage.service';

const apiUrl = environment.API_BASE_URL;
const apiUrlV3 = environment.API_BASE_URL_V3;

@Injectable({
    providedIn: 'root',
})

/**
 * Export Class
 */
export class LoginService {
    /**
     * Constructor
     */
    constructor(
        private router: Router,
        private http: HttpClient,
        private storageService: StorageService,
        private contractService: ContractIntegrationsService,
        private injector: Injector
    ) {

    }


    /**
     *
     * @return {boolean}
     */
    async canActivate() {
        let account: any;
        try {
            const storedItem = this.storageService.getItem('wagmi_new_store');
            account = storedItem ? JSON.parse(storedItem) : null;
        } catch (error) {
            console.error('Error parsing wagmi_new_store:', error);
            this.storageService.removeItem('wagmi_new_store');
            this.router.navigate(['login']);
            return false;
        }

        if (this.hasExpiredSession()) {
            // Signed in earlier but the session has run out: end it properly
            // (toast, server logout, disconnect wallet, back to the login page)
            // instead of loading the page and waiting for the first 401.
            // IdleService is resolved lazily because it depends on this service.
            this.injector.get(IdleService).handleSessionExpired();
            return false;
        }

        if (account?.state?.data && Object.keys(account?.state?.data).length > 0 && account.state.data.account != '') {
            const ownerAddress = await this.contractService.owner();
            if (ownerAddress === account.state.data.account) {
                this.ensureCsrfToken();
                // logged in so return true
                return true;
            }
        }
        // not logged in so redirect to login page with the return url
        this.router.navigate(['login']);
        return false;
    }


    /**
     * True when a session was stored for this admin but its expiry has passed.
     * @returns {boolean}
     */
    private hasExpiredSession(): boolean {
        if (!this.storageService.getItem('token')) return false;

        let expiry = 0;
        try {
            const expiryRaw = this.storageService.getItem('expiryTime');
            if (!expiryRaw) return false;
            const parsed = JSON.parse(expiryRaw);
            expiry = typeof parsed === 'number' ? parsed : new Date(parsed).getTime();
        } catch (_error) {
            return false;
        }

        return Number.isFinite(expiry) && expiry > 0 && expiry <= Date.now();
    }


    /**
     * Signin
     * @param {data} data
     * @returns
     */
    public signin(data: object) {
        return this.http.post(`${apiUrl}/admin/signup-signin-admin`, data);
    }

    /**
     * Request a one-time sign-in challenge (nonce + message) for a wallet.
     * @param {object} data { wallet_address }
     */
    public walletChallenge(data: { wallet_address: string }) {
        return this.http.post(`${apiUrlV3}/admin/auth/wallet/challenge`, data);
    }

    /**
     * Submit the signed challenge for server-side signature verification.
     * @param {object} data { wallet_address, signature, nonce }
     */
    public walletVerify(data: { wallet_address: string; signature: string; nonce: string }) {
        return this.http.post(`${apiUrlV3}/admin/auth/wallet/verify`, data, { withCredentials: true });
    }

    /**
     * Re-fetch the CSRF token for an existing session cookie (e.g. after a
     * page refresh or for sessions issued before the CSRF rollout). The
     * interceptor stores the token from the response.
     */
    public fetchCsrfToken() {
        return this.http.get(`${apiUrlV3}/admin/auth/csrf-token`, { withCredentials: true });
    }

    /**
     * Fire a one-shot CSRF token fetch when the session cookie is still valid
     * but no token is stored locally.
     */
    private ensureCsrfToken(): void {
        if (this.storageService.getItem('csrfToken')) return;

        let expiry = 0;
        try {
            const expiryRaw = this.storageService.getItem('expiryTime');
            expiry = expiryRaw ? Number(JSON.parse(expiryRaw)) : 0;
        } catch (_error) {
            return;
        }

        if (Number.isFinite(expiry) && expiry > Date.now()) {
            this.fetchCsrfToken().subscribe({ error: () => undefined });
        }
    }

    /**
     * Invalidate the current admin session on the server.
     */
    public logout() {
        return this.http.post(`${apiUrlV3}/admin/auth/logout`, {});
    }

}
